import { useEffect, useRef, useState } from 'react';
import ServerDiscovery from '../services/ServerDiscovery';
import { authHeaders } from '../utils/auth';
import { trackEvent } from '../utils/analytics';

const MAX_BYTES = 120 * 1024 * 1024;
const POLL_MS = 2500;
// Transient network failures while polling (phone backgrounded, flaky
// connection) must not abort an analysis that is still running server-side.
const POLL_MAX_CONSECUTIVE_FAILURES = 8;
// Analysis takes roughly a fifth of the clip's length; file size is the
// only clue we have before it finishes. Rough, deliberately pessimistic.
const EST_SECONDS_PER_MB = 6;

function formatElapsed(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * "Or upload a recording": sends an audio/video file to the backend, which
 * runs AssemblyAI's async detection (99+ languages) on the first 10 minutes,
 * then hands the result to the same cards a mic session uses.
 */
export default function UploadDetect({ signedIn, onRequireSignIn, onResult, onError, disabled }) {
  const inputRef = useRef(null);
  const [phase, setPhase] = useState('idle'); // idle | uploading | analyzing
  const [fileName, setFileName] = useState('');
  const [startedAt, setStartedAt] = useState(0);
  const [estimateSec, setEstimateSec] = useState(0);
  const [now, setNow] = useState(0);
  const [hiccup, setHiccup] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => () => { cancelledRef.current = true; }, []);

  // Elapsed-time ticker while a job is running.
  useEffect(() => {
    if (phase === 'idle') return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const pick = () => {
    if (disabled || phase !== 'idle') return;
    if (!signedIn) { onRequireSignIn(); return; }
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      onError('That file is too large (max 120 MB). Trim it to the first 10 minutes and try again.');
      return;
    }
    setFileName(file.name);
    setStartedAt(Date.now());
    setEstimateSec(Math.max(10, Math.round((file.size / 1048576) * EST_SECONDS_PER_MB)));
    setHiccup(false);
    setPhase('uploading');
    trackEvent('upload_start', { type: file.type || 'unknown', sizeMb: Math.round(file.size / 1048576) });
    try {
      const base = await ServerDiscovery.discoverHttpBase();
      const res = await fetch(`${base}/detect/upload`, {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': file.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent(file.name).slice(0, 120),
        },
        body: file,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Upload failed');
      }
      const { jobToken } = await res.json();
      setPhase('analyzing');

      // Poll until AssemblyAI finishes (roughly a fifth of the clip length).
      // Network hiccups are retried; only a server-reported failure, or a
      // long run of consecutive hiccups, ends the job.
      let failures = 0;
      for (;;) {
        await new Promise((r) => setTimeout(r, POLL_MS * Math.min(4, 1 + failures)));
        if (cancelledRef.current) return;
        let data;
        try {
          const poll = await fetch(`${base}/detect/upload/${encodeURIComponent(jobToken)}`, { headers: authHeaders() });
          if (poll.status >= 500) throw new Error('server busy');
          if (!poll.ok) {
            const body = await poll.json().catch(() => ({}));
            throw Object.assign(new Error(typeof body.detail === 'string' ? body.detail : 'Could not check the analysis'), { fatal: true });
          }
          data = await poll.json();
          failures = 0;
          setHiccup(false);
        } catch (pollErr) {
          if (pollErr.fatal) throw pollErr;
          failures += 1;
          setHiccup(true);
          trackEvent('upload_poll_retry', { attempt: failures });
          if (failures >= POLL_MAX_CONSECUTIVE_FAILURES) {
            throw new Error("Lost the connection while analyzing. Your file was received; try again in a moment and keep this tab open.");
          }
          continue;
        }
        if (data.status === 'processing') continue;
        if (data.status === 'error') throw new Error(data.message || 'Analysis failed');
        trackEvent('upload_result', {
          outcome: data.language ? 'language' : 'none',
          language: data.language || '',
          seconds: Math.round((Date.now() - startedAt) / 1000),
        });
        onResult(data);
        return;
      }
    } catch (err) {
      trackEvent('upload_result', { outcome: 'error', seconds: Math.round((Date.now() - startedAt) / 1000), message: String(err.message || '').slice(0, 80) });
      onError(err.message || 'Upload failed');
    } finally {
      if (!cancelledRef.current) setPhase('idle');
    }
  };

  return (
    <div className="upload-detect" id="upload">
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,video/*,.m4a,.mp3,.wav,.ogg,.opus,.webm,.mp4,.mov,.aac,.flac"
        onChange={onFile}
        hidden
      />
      {phase === 'idle' ? (
        <button type="button" className="upload-detect-btn" onClick={pick} disabled={disabled}>
          <UploadIcon />
          Or upload a recording
        </button>
      ) : (
        <div className="upload-detect-status" role="status">
          <span className="upload-detect-spinner" aria-hidden />
          <span className="upload-detect-status-text">
            <strong>{phase === 'uploading' ? 'Uploading' : 'Analyzing'}</strong> {fileName}
            <br />
            <span className="upload-detect-progress">
              {formatElapsed(now - startedAt)} elapsed
              {estimateSec > 0 && ` · usually about ${estimateSec < 60 ? `${estimateSec}s` : `${Math.ceil(estimateSec / 60)} min`} for a file this size`}
              {hiccup && ' · reconnecting…'}
            </span>
            <br />
            <span className="upload-detect-progress">Keep this tab open; large videos can take a few minutes.</span>
          </span>
        </div>
      )}
      <p className="upload-detect-note">Voice notes, voicemails, podcasts, videos. First 10 minutes analyzed, 99+ languages.</p>
    </div>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
